import { APPROVED, SUBMITTED, WORK_LOCATION } from 'constants/worklog';
import type { CalendarItem, WorkStatusSummary } from 'interface/leave';

import { convertMinuteToDisplayFormat } from './time';
import { checkAttendanceUnfrozen } from './attendance';

/**
 * Calculates the total duration of worklogs with status SUBMITTED or APPROVED.
 *
 * @param data[] - The calendar data array.
 * @returns - The total duration formatted as a display string.
 */
export function getWorklogTotalDuration(data: CalendarItem[]): string {
  const totalDuration = data.reduce((acc, cur) => {
    if (![SUBMITTED, APPROVED].includes(cur.worklog?.status)) {
      return acc;
    }

    return (acc += cur.worklog?.totalDuration || 0);
  }, 0);

  return convertMinuteToDisplayFormat(totalDuration);
}

/**
 * Generates a summary of work status data.
 *
 * @param data[] - The calendar data array.
 * @returns - An object containing the summary of work status data.
 */
export function getWorkStatusSummaryData(
  data: CalendarItem[],
  unfreezeRequests: Array<{ unfreezeFrom: string; unfreezeTo: string; updatedAt: string; isUnfrozen: boolean }>
): WorkStatusSummary {
  return data.reduce(
    (acc, item) => {
      const { worklog, leave, date } = item;

      const isAttendanceUnfrozen = checkAttendanceUnfrozen(date, unfreezeRequests);

      if (leave && ((leave.isAutomated && !isAttendanceUnfrozen) || !leave.isAutomated)) {
        acc.leave++;
        return acc;
      }

      if (worklog && worklog.location === WORK_LOCATION.HOME) {
        acc.wfh++;
        return acc;
      }

      if (worklog && worklog.location === WORK_LOCATION.OFFICE) {
        acc.wfo++;
      }

      return acc;
    },
    { wfh: 0, wfo: 0, leave: 0 }
  );
}
